// FLAG:useDocuAi — where the analysis facts behind two receipt-form hints come from:
//   * `taxCalculation` — gross-to-net derivation (grossTaxCalculationHint.ts: „Netto und
//     Umsatzsteuer wurden aus Bruttobeträgen berechnet …" / „… durch Rundung je Zeile …");
//   * `lineTaxAssumed` — several stated rates, none per line (assumedLineTaxHint.ts).
//
// Until plan omxweb-draft-receipt-instead-of-review-row T20 (2026-09-18) receipt-form read both
// off metaData.invoiceFacts in review mode ONLY. Since T16 the booked expense/revenue form shows the
// same stated-totals block as the review, so a gross-derived or assumed drift stood on screen there
// without the sentence that explains it. The same receipt reaches the screen on two paths and the
// facts live in different places along the way (exactly the finding behind unbookableVatFact.ts):
//   • review component (/documents/review/:id) — build-review-data puts them on metaData.invoiceFacts;
//   • expense/revenue form (/expenses/edit/:id) — the receipt carries no facts; they live on the
//     reviewed details the host fetched, or on the attached file (documentInvoiceDetails,
//     metaData.invoiceFacts, metaData.ocr.invoiceFacts).
//
// `enabled` is the STRUCTURAL flag gate: callers pass useDocuAi (or review mode) through; with
// flag-OFF the result is empty and the hints do not exist — not a data argument („no facts exist
// with flag-OFF"), because that would be a claim about every writer of file metaData.
import type { GrossTaxCalculationFact } from './grossTaxCalculationHint'
import type { AssumedLineTaxFact } from './assumedLineTaxHint'

export interface AnalysisFactHints {
  taxCalculation: GrossTaxCalculationFact | null
  lineTaxAssumed: AssumedLineTaxFact | null
}

interface InvoiceFactsLike {
  taxCalculation?: GrossTaxCalculationFact | null
  lineTaxAssumed?: AssumedLineTaxFact | null
  [key: string]: unknown
}

interface FactCarrier {
  invoiceFacts?: InvoiceFactsLike | null
}

interface RelatedFile {
  documentInvoiceDetails?: FactCarrier | null
  metaData?: (FactCarrier & { ocr?: FactCarrier | null }) | null
}

interface FactSources {
  /** metaData.invoiceFacts of the displayed receipt (populated by the review component). */
  invoiceFacts?: InvoiceFactsLike | null
  /** The reviewed record already loaded by the expense/revenue host. */
  reviewDetails?: FactCarrier | null
  /** The receipt's filesToAssign + receipts. */
  relatedFiles?: (RelatedFile | null | undefined)[] | null
}

const NO_FACTS: AnalysisFactHints = { taxCalculation: null, lineTaxAssumed: null }

export const findAnalysisFactHints = (
  enabled: boolean,
  { invoiceFacts, reviewDetails, relatedFiles }: FactSources = {},
): AnalysisFactHints => {
  if (!enabled) return NO_FACTS

  const candidates: (InvoiceFactsLike | null | undefined)[] = [
    invoiceFacts,
    reviewDetails?.invoiceFacts,
    ...(relatedFiles || []).flatMap(file => [
      file?.documentInvoiceDetails?.invoiceFacts,
      file?.metaData?.invoiceFacts,
      file?.metaData?.ocr?.invoiceFacts,
    ]),
  ]

  return {
    taxCalculation: candidates.find(facts => facts?.taxCalculation)?.taxCalculation ?? null,
    lineTaxAssumed: candidates.find(facts => facts?.lineTaxAssumed)?.lineTaxAssumed ?? null,
  }
}
